import React, { useState, useEffect, useContext } from 'react';
import { AuthContext, API } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { TrendingUp, TrendingDown, Sparkles, Lightbulb, Target } from 'lucide-react';

const WeeklyReports = () => {
  const { user } = useContext(AuthContext);
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadChildren();
  }, []);

  useEffect(() => {
    if (selectedChild) {
      loadReport();
    }
  }, [selectedChild]);

  const loadChildren = async () => {
    try {
      const response = await API.get('/children');
      setChildren(response.data);
      if (response.data.length > 0) {
        setSelectedChild(response.data[0].child_id);
      }
    } catch (error) {
      console.error('Failed to load children:', error);
    }
  };

  const loadReport = async () => {
    setLoading(true);
    try {
      const response = await API.get(`/reports/weekly/${selectedChild}`);
      setReport(response.data);
    } catch (error) {
      console.error('Failed to load report:', error);
    } finally {
      setLoading(false);
    }
  };

  if (children.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground mb-4">No children added yet</p>
        <Button onClick={() => window.location.href = '/dashboard'}>Go to Dashboard</Button>
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-5xl">
      <div>
        <h1 className="text-4xl font-bold mb-2">Weekly Reports</h1>
        <p className="text-muted-foreground">AI-powered insights and recommendations</p>
      </div>

      {/* Child Selector */}
      <Card>
        <CardHeader>
          <CardTitle>Select Child</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <Select value={selectedChild} onValueChange={setSelectedChild}>
              <SelectTrigger className="flex-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {children.map(child => (
                  <SelectItem key={child.child_id} value={child.child_id}>
                    {child.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={loadReport} disabled={loading}>
              {loading ? 'Loading...' : 'Refresh Report'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : report && report.logs_count !== 0 ? (
        <>
          {/* Summary */}
          <Card className="bg-gradient-to-br from-violet-50 to-purple-50">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Sparkles className="w-6 h-6 text-violet-600" />
                <CardTitle>Weekly Summary</CardTitle>
              </div>
              <CardDescription>
                {report.week_start} to {report.week_end}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-lg">{report.summary}</p>
            </CardContent>
          </Card>

          {/* Mood & Behavior Trends */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">\n            <Card>
              <CardHeader>
                <CardTitle>Mood Trends</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(report.mood_trends || {}).map(([mood, count]) => (
                    <div key={mood} className="flex items-center justify-between">
                      <span className="capitalize">{mood}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-violet-500"
                            style={{
                              width: `${(count / Math.max(...Object.values(report.mood_trends))) * 100}%`
                            }}
                          />
                        </div>
                        <span className="text-sm font-medium w-8">{count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Behavior Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(report.behavior_summary || {}).map(([behavior, count]) => (
                    <div key={behavior} className="flex items-center justify-between">
                      <span className="capitalize">{behavior.replace('_', ' ')}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-purple-500"
                            style={{
                              width: `${(count / Math.max(...Object.values(report.behavior_summary))) * 100}%`
                            }}
                          />
                        </div>
                        <span className="text-sm font-medium w-8">{count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Insights */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Lightbulb className="w-6 h-6 text-yellow-500" />
                <CardTitle>Key Insights</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {report.insights.map((insight, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-sm font-semibold text-yellow-700">{index + 1}</span>
                    </div>
                    <p className="flex-1">{insight}</p>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Recommendations */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Target className="w-6 h-6 text-blue-500" />
                <CardTitle>Recommendations for Next Week</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {report.recommendations.map((rec, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-sm font-semibold text-blue-700">{index + 1}</span>
                    </div>
                    <p className="flex-1">{rec}</p>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Wins */}
          {report.wins && report.wins.length > 0 && (
            <Card className="bg-gradient-to-br from-green-50 to-emerald-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-6 h-6 text-green-600" />
                  Wins This Week
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {report.wins.map((win, index) => (
                    <Badge key={index} variant="default" className="bg-green-600">
                      {win}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      ) : (
        <Card>
          <CardContent className="text-center py-12">
            <p className="text-muted-foreground">No data available for weekly report. Start logging daily to see insights!</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default WeeklyReports;